import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostesIluminacao } from '../context/PostesIluminacaoProvider';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { createPosteIcon, calculateMapCenter, formatarCoordenadas } from '../utils/mapHelpers';
import { Eye, Lightbulb } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Configurar ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.8.0/images/marker-shadow.png',
});

/**
 * Componente que exibe o mapa de postes de iluminação
 */
const MapaPostes = () => {
  const navigate = useNavigate();
  const { 
    postes, 
    localidades, 
    loading, 
    filtros, 
    setFiltros,
    carregarPostes,
    carregarLocalidades
  } = usePostesIluminacao();

  // Coordenadas de Conceição do Castelo - ES (mesmo padrão dos outros módulos)
  const centroConceicaoCastelo = [-20.359292, -41.247282];
  const [mapCenter, setMapCenter] = useState(centroConceicaoCastelo);
  const [zoom, setZoom] = useState(13);

  // Carregar dados ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      try {
        await carregarLocalidades();
        await carregarPostes();
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    
    fetchData();
  }, [carregarLocalidades, carregarPostes]); // Dependências corretas

  // Recalcular centro do mapa baseado nos postes
  useEffect(() => {
    if (postes.length > 0) {
      const coordinates = postes.map(p => [p.latitude, p.longitude]);
      if (coordinates.length > 0) {
        setMapCenter(calculateMapCenter(postes));
        setZoom(12);
      }
    }
  }, [postes]);

  // Aplicar filtros
  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
  };

  // Limpar filtros
  const limparFiltros = () => {
    setFiltros({
      status: null,
      localidade: null,
      tipoLampada: null,
      busca: null
    });
  };

  // Dados de exemplo para teste (remover depois)
  const postesExemplo = [
    {
      id: 1,
      numero_identificacao: 'P001',
      latitude: -20.359292,
      longitude: -41.247282,
      status: 'Funcionando',
      tipo_lampada: 'LED',
      potencia: '100W',
      altura_poste: '8m',
      localidade_id: 1,
      localidades: { id: 1, nome: 'Centro', tipo: 'Urbana' },
      observacoes: 'Poste em bom estado',
      data_instalacao: '2023-01-15'
    },
    {
      id: 2,
      numero_identificacao: 'P002',
      latitude: -20.360000,
      longitude: -41.248000,
      status: 'Alerta',
      tipo_lampada: 'LED',
      potencia: '150W',
      altura_poste: '10m',
      localidade_id: 2,
      localidades: { id: 2, nome: 'São Pedro', tipo: 'Rural' },
      observacoes: 'Necessita manutenção preventiva',
      data_instalacao: '2023-02-20'
    },
    {
      id: 3,
      numero_identificacao: 'P003',
      latitude: -20.358000,
      longitude: -41.246000,
      status: 'Defeito',
      tipo_lampada: 'Vapor de Sódio',
      potencia: '250W',
      altura_poste: '12m',
      localidade_id: 3,
      localidades: { id: 3, nome: 'Santa Rita', tipo: 'Urbana' },
      observacoes: 'Lâmpada queimada',
      data_instalacao: '2022-11-10'
    }
  ];

  // Usar dados de exemplo se não há postes carregados
  const postesParaUsar = postes.length > 0 ? postes : postesExemplo;

  // Obter tipos de lâmpada únicos
  const tiposLampada = [...new Set(postesParaUsar.map(poste => poste.tipo_lampada).filter(Boolean))];

  // Aplicar filtros localmente usando useMemo para evitar recálculos desnecessários
  const postesFiltrados = useMemo(() => {
    return postesParaUsar.filter(poste => {
      // Filtro por status
      if (filtros.status && poste.status !== filtros.status) {
        return false;
      }
      
      // Filtro por localidade
      if (filtros.localidade && poste.localidade_id !== parseInt(filtros.localidade)) {
        return false;
      }
      
      // Filtro por tipo de lâmpada
      if (filtros.tipoLampada && poste.tipo_lampada !== filtros.tipoLampada) {
        return false;
      }
      
      // Filtro por busca (número de identificação)
      if (filtros.busca && !poste.numero_identificacao.toLowerCase().includes(filtros.busca.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  }, [postesParaUsar, filtros.status, filtros.localidade, filtros.tipoLampada, filtros.busca]);

  // Recalcular centro quando os filtros mudarem
  useEffect(() => {
    if (postesFiltrados.length > 0) {
      const center = calculateMapCenter(postesFiltrados);
      setMapCenter(center);
    }
  }, [postesFiltrados]);

  // Debug inicial apenas
  useEffect(() => {
    console.log('Componente MapaPostes montado');
    console.log('Postes disponíveis:', postesParaUsar.length);
  }, []);

  return (
    <div style={{ 
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* Header compacto - seguindo o padrão dos outros módulos */}
      <div style={{ 
        padding: '8px 15px', 
        backgroundColor: 'white', 
        borderBottom: '1px solid #dee2e6',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexShrink: 0,
        zIndex: 1000
      }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#ff6f00' }}>Mapa de Postes de Iluminação</h2>
        <p style={{ color: '#666', margin: 0, fontSize: '12px' }}>
          Clique nos marcadores para informações.
          <span style={{ marginLeft: '8px', fontWeight: '500' }}>
            Exibindo {postesFiltrados.length} de {postesParaUsar.length} postes
          </span>
          <span style={{ marginLeft: '8px' }}>
            <span style={{ color: '#4caf50', fontWeight: 'bold' }}>■ Funcionando</span>
            <span style={{ color: '#ff9800', fontWeight: 'bold', marginLeft: '6px' }}>■ Alerta</span>
            <span style={{ color: '#f44336', fontWeight: 'bold', marginLeft: '6px' }}>■ Defeito</span>
          </span>
        </p>
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '10px', 
          marginTop: '10px',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '5px', fontWeight: '500' }}>Status:</label>
            <select 
              name="status" 
              value={filtros.status || ''}
              onChange={handleFiltroChange}
              style={{ 
                padding: '6px 10px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">Todos</option>
              <option value="Funcionando">Funcionando</option>
              <option value="Alerta">Alerta</option>
              <option value="Defeito">Defeito</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '5px', fontWeight: '500' }}>Bairro:</label>
            <select 
              name="localidade" 
              value={filtros.localidade || ''}
              onChange={handleFiltroChange}
              style={{ 
                padding: '6px 10px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">Todas</option>
              {localidades.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.nome}</option>
              ))}
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ marginRight: '5px', fontWeight: '500' }}>Tipo:</label>
            <select 
              name="tipoLampada" 
              value={filtros.tipoLampada || ''}
              onChange={handleFiltroChange}
              style={{ 
                padding: '6px 10px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px', 
                fontSize: '14px' 
              }}
            >
              <option value="">Todos</option>
              {tiposLampada.map(tipo => (
                <option key={tipo} value={tipo}>{tipo}</option>
              ))}
            </select>
          </div>
          
          <button 
            onClick={limparFiltros}
            style={{ 
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              border: '1px solid #ced4da',
              backgroundColor: '#f8f9fa',
              color: '#333'
            }}
          >
            Limpar Filtros
          </button>
        </div>
      </div>
      
      {/* Mapa ocupando todo o espaço restante - seguindo o padrão dos outros módulos */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%' 
          }}>
            <div style={{ 
              border: '4px solid rgba(0, 0, 0, 0.1)',
              borderLeftColor: '#ff6f00',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '10px' }}>Carregando dados do mapa...</p>
          </div>
        ) : postesFiltrados.length === 0 && postesParaUsar.length > 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <h3 style={{ margin: '0 0 8px 0', color: '#333' }}>
              Nenhum poste encontrado
            </h3>
            <p style={{ margin: '0 0 20px 0', color: '#666', maxWidth: '400px' }}>
              Não foram encontrados postes com os filtros aplicados. Tente ajustar os filtros de busca.
            </p>
            <button
              onClick={limparFiltros}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                backgroundColor: '#f5f5f5',
                color: '#333',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#eeeeee'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            >
              Limpar Filtros
            </button>
          </div>
        ) : (
          <>
            <MapContainer 
              center={mapCenter} 
              zoom={zoom} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              {postesFiltrados.map((poste, index) => {
                // Validar coordenadas
                const lat = parseFloat(poste.latitude);
                const lng = parseFloat(poste.longitude);
                
                if (isNaN(lat) || isNaN(lng)) {
                  console.warn('Coordenadas inválidas para poste:', poste.id, lat, lng);
                  return null;
                }
                
                return (
                  <Marker 
                    key={`poste-${poste.id}`}
                    position={[lat, lng]} 
                    icon={createPosteIcon(poste.status)}
                  >
                    <Popup>
                      <div style={{ minWidth: '200px' }}>
                        <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>
                          <Lightbulb size={16} style={{ verticalAlign: 'middle', marginRight: '5px' }} />
                          Poste #{poste.numero_identificacao}
                        </h4>
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Status:</strong> 
                          <span style={{ 
                            color: poste.status === 'Funcionando' ? '#4caf50' : 
                                   poste.status === 'Alerta' ? '#ff9800' : '#f44336',
                            marginLeft: '5px'
                          }}>
                            {poste.status}
                          </span>
                        </p>
                        {poste.tipo_lampada && (
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Tipo:</strong> {poste.tipo_lampada}
                          </p>
                        )}
                        <p style={{ margin: '5px 0', fontSize: '14px' }}>
                          <strong>Bairro:</strong> {poste.ccbairros?.descricao || 'Não informado'}
                        </p>
                        {poste.altura_poste && (
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Altura:</strong> {poste.altura_poste}
                          </p>
                        )}
                        {poste.potencia && (
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Potência:</strong> {poste.potencia}
                          </p>
                        )}
                        {poste.observacoes && (
                          <p style={{ margin: '5px 0', fontSize: '14px' }}>
                            <strong>Observações:</strong> {poste.observacoes}
                          </p>
                        )}
                        <button 
                          style={{ 
                            marginTop: '10px', 
                            fontSize: '12px', 
                            padding: '6px 12px',
                            backgroundColor: '#ff6f00',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          onClick={() => {
                            console.log('Clique no botão - ID do poste:', poste.id);
                            navigate(`/postes-iluminacao/detalhes/${poste.id}`);
                          }}
                        >
                          <Eye size={14} />
                          Ver Detalhes
                        </button>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
            
            {/* Legenda do mapa - seguindo o padrão dos outros módulos */}
            <div style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              backgroundColor: 'white',
              padding: '10px',
              borderRadius: '4px',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              zIndex: 1000
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Legenda</h4>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                <span style={{ 
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  marginRight: '5px',
                  backgroundColor: '#4caf50',
                  border: '1px solid white',
                  boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)'
                }}></span>
                <span>Funcionando</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                <span style={{ 
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  marginRight: '5px',
                  backgroundColor: '#ff9800',
                  border: '1px solid white',
                  boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)'
                }}></span>
                <span>Alerta</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                <span style={{ 
                  display: 'inline-block',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  marginRight: '5px',
                  backgroundColor: '#f44336',
                  border: '1px solid white',
                  boxShadow: '0 0 2px rgba(0, 0, 0, 0.3)'
                }}></span>
                <span>Defeito</span>
              </div>
            </div>
          </>
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default MapaPostes;